import { ChevronDown, Settings } from 'lucide-react';
import {
  type CSSProperties,
  memo,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useLocalStorage } from 'react-use';

import Avatar from '@/components/Avatar/Avatar';
import Button from '@/components/Button/Button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
} from '@/components/shadcn/ui/accordion';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '@/components/shadcn/ui/sidebar';
import STORAGE_KEYS from '@/configs/local-storage-keys';
import { canShowContactsMenu } from '@/entities/workspace';
import {
  getWorkspaceSidebarSchema,
  type WorkspaceSidebarGroupItem,
  type WorkspaceSidebarItem,
  type WorkspaceSidebarLeafItem,
} from '@/features/Commons/Sidebar/model/sidebar-schema';
import { useFaceCream } from '@/hooks/domain/customWorkspace/useFaceCream';
import useColor from '@/hooks/domain/useColor';
import useUserRole from '@/hooks/domain/useUserRole';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useKeymap from '@/hooks/pure/useKeymap';
import { getWorkspaceStorageItem } from '@/hooks/pure/useWorkspaceStorage';
import { cn } from '@/lib/cn';
import {
  SIDEBAR_FULL_WIDTH,
  SIDEBAR_SHRINKED_WIDTH,
} from '@/stores/pure/UIStore';
import useUserStore from '@/stores/UserStore';
import { useSidebarShrinked } from '@/stores/WorkspaceCacheStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { isProdMode } from '@/utils/environment';

import GlobalSearchButton from './GlobalSearchButton';
import NavigatorButton from './NavigatorButton';
import SidebarToggleButton from './SidebarToggleButton';
import WorkspaceSelector from './WorkspaceSelector';

type SidebarAccordionState = {
  heySynclyAccordion: boolean;
  managementAccordion: boolean;
};

const accordionStateKeyMap = {
  'hey-syncly': 'heySynclyAccordion',
  managements: 'managementAccordion',
} as const satisfies Record<string, keyof SidebarAccordionState>;

const defaultAccordionState: SidebarAccordionState = {
  heySynclyAccordion: true,
  managementAccordion: true,
};

type SidebarAccordionGroupKey = keyof typeof accordionStateKeyMap;

const SidebarShell = () => {
  const isFaceCream = useFaceCream();
  const color = useColor();
  const location = useLocation();
  const wr = useWorkspaceRouter();
  const params = useParams();
  const { isEditor } = useUserRole();
  const [sidebarState, setSidebarState] =
    useLocalStorage<SidebarAccordionState>(
      STORAGE_KEYS.SIDEBAR_STATE,
      defaultAccordionState,
    );

  const { user } = useUserStore((state) => ({
    user: state.user,
  }));
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const { shrinked, setShrinked } = useSidebarShrinked();

  const showNavigatorButton = true;
  const showCustomerMenu = canShowContactsMenu(params.workspace_id);
  const sidebarSchema = useMemo(() => {
    return getWorkspaceSidebarSchema({
      isEditor,
      isFaceCream,
      isProdMode: isProdMode(),
      hideCustomer: !showCustomerMenu,
    });
  }, [isEditor, isFaceCream, showCustomerMenu]);

  useKeymap('KeyS', () => setShrinked(!shrinked), {
    ctrl: true,
  });

  useEffect(() => {
    if (!currWorkspace || !sidebarState) return;

    const nextState = Object.keys(sidebarState).reduce(
      (acc, key) => ({
        ...acc,
        [key]: (() => {
          const storedValue = getWorkspaceStorageItem<string | null>(key, null);
          if (storedValue === null) {
            return sidebarState[key as keyof SidebarAccordionState];
          }

          return storedValue === '1';
        })(),
      }),
      {} as SidebarAccordionState,
    );

    setSidebarState(nextState);
  }, [currWorkspace, setSidebarState]);

  useEffect(() => {
    if (!user) {
      wr.navigate('/login', {
        replace: true,
      });
    }
  }, [user, wr]);

  const getAccordionKey = useCallback(
    (groupKey: WorkspaceSidebarGroupItem['key']) =>
      groupKey in accordionStateKeyMap
        ? accordionStateKeyMap[groupKey as SidebarAccordionGroupKey]
        : undefined,
    [],
  );

  const updateGroupOpen = useCallback(
    (groupKey: WorkspaceSidebarGroupItem['key'], open: boolean) => {
      if (!sidebarState) return;

      const storageKey = getAccordionKey(groupKey);
      if (!storageKey) return;

      setSidebarState({
        ...sidebarState,
        [storageKey]: open,
      });
    },
    [getAccordionKey, setSidebarState, sidebarState],
  );

  const getRoutePrefix = useCallback((href: string) => {
    if (href === '/home') return href;

    const [segment] = href.split('/').filter(Boolean);
    return segment ? `/${segment}` : href;
  }, []);

  const isLeafSelected = useCallback(
    (item: WorkspaceSidebarLeafItem) => {
      const workspacePrefix = `/workspace/${params.workspace_id}`;
      if (item.href === '/home') {
        return location.pathname === `${workspacePrefix}/home`;
      }

      return location.pathname.includes(
        `${workspacePrefix}${getRoutePrefix(item.href)}`,
      );
    },
    [getRoutePrefix, location.pathname, params.workspace_id],
  );

  const isGroupSelected = useCallback(
    (item: WorkspaceSidebarGroupItem) =>
      Boolean(
        (item.href &&
          location.pathname.includes(
            `/workspace/${params.workspace_id}${getRoutePrefix(item.href)}`,
          )) ||
        item.children.some((child) => isLeafSelected(child)),
      ),
    [getRoutePrefix, isLeafSelected, location.pathname, params.workspace_id],
  );

  const renderLeafItem = useCallback(
    (item: WorkspaceSidebarLeafItem) => {
      const selected = isLeafSelected(item);

      return (
        <SidebarMenuItem key={item.key}>
          <SidebarMenuButton
            className="cursor-pointer"
            type="button"
            title={item.label}
            data-active={selected}
            onClick={() => {
              wr.push(item.href);
            }}
          >
            {item.IconComponent ? (
              <span className="shrink-0">
                {<item.IconComponent className="size-4 text-foreground" />}
              </span>
            ) : null}
            {!shrinked ? <span className="truncate">{item.label}</span> : null}
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    },
    [isLeafSelected, shrinked, wr],
  );

  const renderGroupItem = useCallback(
    (item: WorkspaceSidebarGroupItem) => {
      const selected = isGroupSelected(item);
      const storageKey = getAccordionKey(item.key);
      const storedOpen = storageKey
        ? (sidebarState?.[storageKey] ?? false)
        : false;
      const open = !shrinked && storedOpen;
      const contentValue = open ? item.key : undefined;

      const handleGroupClick = () => {
        if (item.href) {
          if (shrinked) {
            setShrinked(false);
          }

          updateGroupOpen(item.key, true);
          wr.push(item.href);
          return;
        }

        if (shrinked) {
          setShrinked(false);
          updateGroupOpen(item.key, true);
          return;
        }

        updateGroupOpen(item.key, !open);
      };

      return (
        <SidebarMenuItem key={item.key}>
          <Accordion
            type="single"
            collapsible
            value={contentValue}
            className="w-full"
          >
            <AccordionItem value={item.key} className="border-none">
              <SidebarMenuButton
                className="cursor-pointer"
                type="button"
                title={item.label}
                data-active={selected}
                onClick={handleGroupClick}
              >
                {item.IconComponent ? (
                  <span className="shrink-0">
                    {<item.IconComponent className="size-4 text-foreground" />}
                  </span>
                ) : null}
                {!shrinked ? (
                  <>
                    <span className="truncate">{item.label}</span>
                    {item.hideChevron ? null : (
                      <ChevronDown
                        className={cn(
                          'ml-auto size-4 transition-transform',
                          open ? 'rotate-180' : '-rotate-90',
                        )}
                      />
                    )}
                  </>
                ) : null}
              </SidebarMenuButton>

              <AccordionContent className="pb-0">
                {!shrinked && (
                  <SidebarMenu>
                    {item.children.map((child) => renderLeafItem(child))}
                  </SidebarMenu>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </SidebarMenuItem>
      );
    },
    [
      getAccordionKey,
      isGroupSelected,
      renderLeafItem,
      setShrinked,
      shrinked,
      sidebarState,
      updateGroupOpen,
    ],
  );

  const renderSidebarItem = useCallback(
    (item: WorkspaceSidebarItem) =>
      item.kind === 'group' ? renderGroupItem(item) : renderLeafItem(item),
    [renderGroupItem, renderLeafItem],
  );

  if (!currWorkspace) return null;

  return (
    <div className={cn('absolute inset-y-0 left-0 z-50 h-full bg-sidebar')}>
      <SidebarProvider
        open={!shrinked}
        style={
          {
            '--sidebar-width': `${SIDEBAR_FULL_WIDTH}px`,
            '--sidebar-width-icon': `${SIDEBAR_SHRINKED_WIDTH}px`,
          } as CSSProperties
        }
      >
        <Sidebar collapsible="icon">
          <SidebarHeader className="py-4 border-none">
            <div
              className={cn(
                'flex items-center gap-2',
                shrinked ? 'flex-col justify-center' : 'flex-row',
              )}
            >
              <div className={cn('min-w-0', shrinked ? 'w-auto' : 'flex-1')}>
                <WorkspaceSelector
                  shrinked={shrinked}
                  enabled={!!user}
                  workspaceName={currWorkspace.name || '?'}
                />
              </div>
              <div
                className={cn([
                  'flex items-center',
                  shrinked ? 'flex-col justify-center' : 'flex-row',
                ])}
              >
                {showNavigatorButton ? (
                  <div
                    className={cn(
                      'shrink-0',
                      shrinked && 'w-full flex justify-center',
                    )}
                  >
                    <NavigatorButton shrinked={shrinked} />
                  </div>
                ) : null}
                <div
                  className={cn(
                    'shrink-0',
                    shrinked && 'w-full flex justify-center',
                  )}
                >
                  <SidebarToggleButton
                    shrinked={shrinked}
                    onClick={() => {
                      setShrinked(!shrinked);
                    }}
                  />
                </div>
              </div>
            </div>
          </SidebarHeader>

          {sidebarSchema.showSearch ? (
            <SidebarHeader className="border-none">
              <div className={cn('flex items-center gap-2')}>
                <GlobalSearchButton shrinked={shrinked} />
              </div>
            </SidebarHeader>
          ) : null}

          <SidebarContent>
            {sidebarSchema.groups.map((group) => (
              <SidebarGroup key={group.key}>
                {group.label ? (
                  <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
                ) : null}
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map(renderSidebarItem)}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>

          <SidebarFooter className={cn('px-3 py-3 border-none')}>
            <div
              className={cn(
                'flex items-center gap-2 px-1 py-1',
                shrinked ? 'justify-center' : 'justify-between',
              )}
            >
              {!shrinked && (
                <div className={cn('flex min-w-0 flex-1 items-center gap-2')}>
                  <Avatar
                    character={user?.firstName[0] || '?'}
                    size={32}
                    color={color.profile[user?.color || 0]}
                    fontSize="16px"
                  />
                  <div className={cn('flex min-w-0 flex-1 flex-col gap-0.5')}>
                    <span
                      className={cn(
                        'truncate text-[13px] font-medium text-gray-800',
                      )}
                    >
                      {`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()}
                    </span>
                    <span className={cn('truncate text-[12px] text-gray-600')}>
                      {user?.email}
                    </span>
                  </div>
                </div>
              )}

              <Button
                variant="ghost_secondary"
                sizeType="m"
                leftIcon={<Settings />}
                onClick={() => {
                  wr.push('/settings/my-account');
                }}
              />
            </div>
          </SidebarFooter>
        </Sidebar>
      </SidebarProvider>
    </div>
  );
};

export default memo(SidebarShell);
